import { useEffect, useState } from "react";

import { useVariation } from "@hackler/react-sdk";

import {
  HACKLE_DEVTOOLS_STORAGE_KEY,
  HACKLE_UNASSIGNED_VARIATION,
  HACKLE_VARIATIONS,
  registerExperiment,
} from "../constants/hackle";
import { customEvent } from "../utils/customEvent";
import { useIsDevEnvironment } from "./useIsDevEnvironment";

type VariationMap = Record<number, string>;

// 발화한 "{key}-{변이}" 집합. 모듈 스코프인 이유: 같은 페이지에서 훅을 두 번 호출해도
// (페이지가 CTA 링크에 variation을 붙이려고 호출 + 카드 내부 호출) 노출이 1회만 나가야 한다.
// 컴포넌트별 useRef로는 인스턴스마다 따로 세어서 모수가 부풀어 판정이 깨진다.
// ponytail: 모듈 스코프라 SPA 라우팅으로 다른 장례식장 페이지로 이동해도 재발화하지 않는다.
// 노출(모수 마킹)은 1회가 맞으므로 의도된 동작이다. 페이지뷰 단위 집계가 필요해지면
// 여기에 pathname을 키에 포함시키면 된다.
const firedExposures = new Set<string>();

/**
 * Hackle 변이 할당 래퍼. platform goi_common의 useCustomVariation과 동일 역할.
 *
 * raw useVariation 대비 세 가지를 더 한다.
 * 1. 미확정 sentinel "Z" - SDK 기본값 "A"로는 로딩 중과 대조군 배정이 구분되지 않는다
 * 2. dev/preview 환경 localStorage 오버라이드 - UI를 눈으로 보기 위한 수단
 * 3. 변이 확정 시 experiment_exposure 1회 발화 (GTM dataLayer + Hackle)
 *
 * dev 오버라이드 사용법 (localhost 또는 Vercel preview에서만 동작):
 *   localStorage.setItem("local_test_variations", '{"6":"B"}'); location.reload();
 * 되돌리기:
 *   localStorage.removeItem("local_test_variations"); location.reload();
 *
 * 주의: 오버라이드는 SDK 배정을 우회한다(effect에서 localStorage를 읽어 clientVariation을
 * 직접 세팅). 즉 오버라이드로 UI가 뜨는 것은 **prod 경로 검증이 아니다.** 실제 배정이
 * 뜨는지 보려면 오버라이드를 지우고 Hackle 콘솔 userOverrides로 본인을 해당 변이에
 * 강제 배정한 뒤 확인해야 한다.
 *
 * Hackle SDK도 useVariation 호출 시 노출을 자동 기록하지만, 그 기록은 도메인별 $id에 묶인다.
 * Hackle 쿠키(_hackle_id/_hackle_hid)는 현재 호스트의 등록 도메인까지만 심을 수 있어
 * dogsound와 goifuneral의 $id가 서로 다르다. 그래서 Hackle 대시보드만으로는 goifuneral에서
 * 일어나는 회원가입·리드를 variation과 조인할 수 없다.
 * experiment_exposure를 GA4로 보내는 이유가 그것이다. BQ에서 variation별 리드를 쪼개는
 * 유일한 다리다. 대신 GTM 트리거·GA4 이벤트 등록이 포털별 컨테이너 6개에 각각 필요하다.
 *
 * 오버라이드는 콘솔 대신 GoiDevTools 패널(우측 플로팅 버튼)로도 조작할 수 있다.
 * dev/preview에서 이 훅이 호출되면 experimentKey를 registerExperiment로 등록해 패널 목록에 띄운다.
 */
export const useCustomVariation = (
  experimentKey: number,
  defaultVariation: string = HACKLE_UNASSIGNED_VARIATION
): string => {
  const variation = useVariation(experimentKey, defaultVariation);
  const isDevEnvironment = useIsDevEnvironment();

  const [clientVariation, setClientVariation] = useState(defaultVariation);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isDevEnvironment) return;

    registerExperiment(experimentKey);

    const stored = window.localStorage.getItem(HACKLE_DEVTOOLS_STORAGE_KEY);
    if (stored) {
      try {
        const variations: VariationMap = JSON.parse(stored);
        const testVariation = variations[experimentKey];

        if ((HACKLE_VARIATIONS as readonly string[]).includes(testVariation)) {
          setClientVariation(testVariation);
          return;
        }
      } catch {
        // 손상된 localStorage 값은 무시하고 서버 변이로 폴백
      }
    }

    setClientVariation(variation);
  }, [experimentKey, isDevEnvironment, variation]);

  const resolvedVariation = isDevEnvironment ? clientVariation : variation;

  // 확정 변이당 1회만 발화. 리렌더·중복 호출로 여러 번 나가면 모수가 부풀어 판정이 깨진다.
  useEffect(() => {
    if (resolvedVariation === HACKLE_UNASSIGNED_VARIATION) return;

    const experimentVariation = `${experimentKey}-${resolvedVariation}`;
    if (firedExposures.has(experimentVariation)) return;
    firedExposures.add(experimentVariation);

    customEvent({
      action: "experiment_exposure",
      properties: { experiment_variation: experimentVariation },
    });
  }, [experimentKey, resolvedVariation]);

  return resolvedVariation;
};
